/**
 * Overview — the one screen that answers "could we hand an auditor a pack today?"
 *
 * ── Why this page leads with a refusal ──
 *
 * The obvious design for a compliance home page is a big green readiness score.
 * This one leads with the honest answer instead, because the product's entire
 * value proposition is that it will not overstate what the evidence shows. A
 * dashboard that renders "78% ready" over three critical failures and two
 * unverifiable controls is doing the thing the product exists to prevent — and
 * once the home page rounds the truth off, nothing further down is trusted either.
 *
 * So the headline states the blocking answer, names the controls responsible, and
 * links each one to the work that would change it. The pleasant number (3 of 8
 * controls verified) is present, and subordinate.
 *
 * ── Second: the loop, always ──
 *
 * The stage strip is on the home page permanently, not just in an empty state.
 * The UX audit's central finding was that a cold user "has to already know the
 * workflow-candidate/oracle model to find the 'connect my workflow' path". Six
 * labelled steps, each linking to the surface that performs it, is the cheapest
 * possible fix for that: the home page teaches the causal chain every time it is
 * opened.
 */

import { useMemo } from "react";
import { aggregateVerificationStatus } from "@excellent/core/verification-product";
import type { VerificationStatus as VerificationStatusLike, VerificationCheckResult } from "@excellent/core/verification-product";
type AuthorityLike = VerificationCheckResult["authority"];
import {
  CHECKS,
  ARTIFACTS,
  METRICS,
  METRIC_TONE,
  RECEIPTS,
  RUNS,
  SOURCES,
  TENANT,
  actor,
  fmtDate,
  fmtDateTime,
  fmtRelative,
  type Finding,
  type VerificationStatus,
} from "./data";
import { href } from "./router";
import {
  Badge,
  Button,
  Callout,
  Card,
  Grid,
  Icon,
  Meter,
  PageShell,
  Pipeline,
  Section,
  Severity,
  Stat,
  Verdict,
} from "./ui";
import { myQueue, openFindings, packBlockers, useStore, worstFirst } from "./store";

export function OverviewPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const open = openFindings(store.findings);
  const mine = myQueue(store.findings);
  const blockers = packBlockers(store.findings, store.controls);

  // ── The card below says "the aggregate of each control's checks". It now IS.
  //
  // This counted `c.status` — a hand-set fixture field — while claiming to be an
  // aggregate. Measured before changing it, 3 of 8 controls disagreed with
  // `aggregateVerificationStatus` over their own checks, and TWO of those read
  // PASS in the fixture while the engine said FAIL and UNKNOWN. On a readiness
  // dashboard that is the worst direction for the error to run: it inflates the
  // headline "controls carrying a current receipt" number.
  //
  // The engine's rule has a clause a local re-implementation almost never has:
  // all-PASS still aggregates to UNKNOWN unless some check carries `deterministic`
  // or `human` authority. A model alone cannot make a control green.
  const controlStatus = useMemo(() => {
    const byControl = new Map<string, { status: VerificationStatusLike; authority: AuthorityLike }[]>();
    for (const k of CHECKS) {
      const list = byControl.get(k.controlId) ?? [];
      list.push({ status: k.status, authority: k.authority });
      byControl.set(k.controlId, list);
    }
    const out = new Map<string, VerificationStatus>();
    for (const c of store.controls) {
      out.set(c.id, aggregateVerificationStatus(byControl.get(c.id) ?? []) as VerificationStatus);
    }
    return out;
  }, [store.controls]);

  const rollup = useMemo(() => {
    const r = { PASS: 0, FAIL: 0, UNKNOWN: 0, NOT_RUN: 0 };
    for (const c of store.controls) {
      const status = (controlStatus.get(c.id) ?? c.status) as keyof typeof r;
      r[status] += 1;
    }
    return r;
  }, [store.controls, controlStatus]);

  const unhealthy = SOURCES.filter((s) => s.health !== "healthy");
  const ready = blockers.length === 0;
  // Bound the element rather than indexing inside the JSX — `mine[0]` is typed
  // possibly-undefined under `noUncheckedIndexedAccess`, and a `length > 0` test
  // in a neighbouring expression does not narrow it.
  const [oldestMine] = worstFirst(mine);

  return (
    <PageShell
      wide
      eyebrow="Overview"
        title={`${TENANT.framework} readiness`}
        lede={
          <>
            Observation window {fmtDate(TENANT.periodStart)} – {fmtDate(TENANT.periodEnd)}. Everything on this
            page is derived from receipts and open findings — nothing is entered by hand, and nothing is
            rounded up.
          </>
        }
        actions={
          <>
            <Button icon="refresh" onClick={() => store.toast("Full control sweep queued across 8 controls.")}>
              Run all controls
            </Button>
            <Button variant="primary" icon="package" onClick={() => onNavigate(href("packs"))}>
              Compose a pack
            </Button>
          </>
        }
    >

      {/* ── The honest headline ──────────────────────────────────────────── */}
      {ready ? (
        <Callout tone="pass" icon="check" title="Every control in scope carries a current receipt.">
          A pack composed today would represent all {store.controls.length} controls as verified, with no
          stated limitations.
        </Callout>
      ) : (
        <Callout
          tone="fail"
          icon="alert"
          title={`Not ready to hand over. ${blockers.length} of ${store.controls.length} controls cannot be evidenced today.`}
        >
          <p style={{ margin: "0 0 calc(var(--spacing) * 2)" }}>
            A pack composed right now would have to exclude these controls or carry them as stated
            limitations. It would still be a valid, signed artifact — it just would not claim they passed.
          </p>
          <div className="vp-inline">
            {blockers.map((b) => (
              <button
                key={b.controlId}
                type="button"
                className="vp-btn"
                data-variant="outline"
                data-size="sm"
                onClick={() => onNavigate(href("controls", b.controlId))}
                title={b.reason}
              >
                {b.ref} — {b.reason}
              </button>
            ))}
          </div>
        </Callout>
      )}

      {/* ── The loop ─────────────────────────────────────────────────────── */}
      <Section
        title="How a verdict gets made"
        note="Each step is a surface in the rail. Click through to see the current state of that step."
      >
        <Card flush>
          <Pipeline
            stages={[
              {
                name: "Connect a workflow",
                desc: "Name what must be true and the independent oracle that can check it.",
                state: "done",
              },
              { name: "Collect evidence", desc: "Read-only connectors snapshot the source of truth.", state: "done" },
              { name: "Evaluate policy", desc: "Versioned, checkable logic runs against the evidence.", state: "done" },
              {
                name: "Decide exceptions",
                desc: `${open.length} FAIL/UNKNOWN findings need a human decision.`,
                state: "active",
              },
              {
                name: "Issue receipts",
                desc: "Bind the verdict to its evidence hashes, policy version and key.",
                state: "done",
              },
              {
                name: "Export a pack",
                desc: ready ? "Ready to compose." : "Blocked until the open controls are settled.",
                state: ready ? "done" : "blocked",
              },
            ]}
          />
        </Card>
      </Section>

      {/* ── Numbers ──────────────────────────────────────────────────────── */}
      <Section title="Where the period stands">
        <Grid cols={4}>
          <Stat
            label="Controls verified"
            icon="shield"
            value={`${rollup.PASS} / ${store.controls.length}`}
            tone={rollup.PASS === store.controls.length ? "pass" : "fail"}
            foot={`${rollup.FAIL} FAIL · ${rollup.UNKNOWN} UNKNOWN · ${rollup.NOT_RUN} never run`}
            onClick={() => onNavigate(href("controls"))}
          />
          <Stat
            label="Awaiting your decision"
            icon="inbox"
            value={mine.length}
            tone={mine.length > 0 ? "accent" : "pass"}
            foot={oldestMine ? `Oldest opened ${fmtRelative(oldestMine.openedAt)}` : "Queue clear"}
            onClick={() => onNavigate(href("queue"))}
          />
          <Stat
            label="Sources healthy"
            icon="plug"
            value={`${SOURCES.length - unhealthy.length} / ${SOURCES.length}`}
            tone={unhealthy.length > 0 ? "unknown" : "pass"}
            foot={
              unhealthy.length > 0
                ? unhealthy.map((s) => `${s.displayName.split(" —")[0]} ${s.health}`).join(" · ")
                : "All inside their freshness window"
            }
            onClick={() => onNavigate(href("connectors"))}
          />
          <Stat
            label="Receipts issued"
            icon="seal"
            value={RECEIPTS.length}
            foot={`All ${RECEIPTS.length} replay-verified offline`}
            onClick={() => onNavigate(href("receipts"))}
          />
        </Grid>
      </Section>

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 6.5)" }}>
        <div className="vp-stack">
          {/* ── Control verdicts ──────────────────────────────────────── */}
          <Card
            title="Control verdicts"
            subtitle="The aggregate of each control's checks under its active policy version."
            actions={
              <Button size="sm" variant="ghost" icon="arrow-right" onClick={() => onNavigate(href("controls"))}>
                All controls
              </Button>
            }
          >
            <Meter
              segments={[
                { tone: "pass", value: rollup.PASS, label: "PASS" },
                { tone: "fail", value: rollup.FAIL, label: "FAIL" },
                { tone: "unknown", value: rollup.UNKNOWN, label: "UNKNOWN" },
                { tone: "waived", value: rollup.NOT_RUN, label: "NOT RUN" },
              ]}
            />
            <hr className="vp-divider" />
            <div className="vp-rows" style={{ margin: "calc(var(--spacing) * -3) calc(var(--spacing) * -4) calc(var(--spacing) * -4)" }}>
              {store.controls.map((c) => {
                const controlOpen = open.filter((f) => f.controlId === c.id);
                const status = controlStatus.get(c.id) ?? c.status;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("controls", c.id))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">
                        <span className="vp-mono" style={{ marginRight: "calc(var(--spacing) * 2)", color: "var(--ink-3)" }}>
                          {c.ref}
                        </span>
                        {c.title}
                      </span>
                      <span className="vp-row-sub">
                        {c.family} · owner {actor(c.ownerActorId).name} ·{" "}
                        {controlOpen.length > 0
                          ? `${controlOpen.length} open finding${controlOpen.length === 1 ? "" : "s"}`
                          : "no open findings"}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Severity level={c.consequence} />
                      <Verdict status={status} />
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* ── Today ────────────────────────────────────────────────────── */}
          <Card
            title="Today"
            subtitle="Runs and receipts from the last 24 hours, newest first."
            actions={
              <Button size="sm" variant="ghost" icon="arrow-right" onClick={() => onNavigate(href("runs"))}>
                All runs
              </Button>
            }
            flush
          >
            <div className="vp-rows">
              {RUNS.slice(0, 6).map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="vp-row"
                  data-clickable="true"
                  onClick={() => onNavigate(href("runs", r.id))}
                >
                  <span className="vp-row-main">
                    <span className="vp-row-title">{r.label}</span>
                    <span className="vp-row-sub">
                      <span className="vp-mono">{r.id}</span> · {r.trigger.replace("_", " ")} ·{" "}
                      {fmtRelative(r.startedAt)}
                      {r.receiptId ? (
                        <>
                          {" "}
                          · receipt <span className="vp-mono">{r.receiptId}</span>
                        </>
                      ) : (
                        " · no receipt issued"
                      )}
                    </span>
                  </span>
                  <span className="vp-row-aside">
                    <Verdict status={r.aggregate} />
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* ── Aside ─────────────────────────────────────────────────────── */}
        <aside className="vp-split-aside">
          <NextActions onNavigate={onNavigate} open={open} />

          <Card
            title="Scorecard"
            subtitle="Every metric currently off target or on warning. The Scorecard page separates the two."
            flush
          >
            <div className="vp-rows">
              {METRICS.filter((m) => m.status !== "pass").map((m) => (
                <div key={m.id} className="vp-row">
                  <span className="vp-row-main">
                    <span className="vp-row-title">{m.label}</span>
                    <span className="vp-row-sub">Target {m.target}</span>
                  </span>
                  <span className="vp-row-aside">
                    <Badge tone={METRIC_TONE[m.status]}>{m.value}</Badge>
                  </span>
                </div>
              ))}
            </div>
            <div className="vp-card-foot">
              <Button size="sm" variant="ghost" icon="chart" onClick={() => onNavigate(href("scorecard"))}>
                All nine metrics
              </Button>
            </div>
          </Card>

          <Card title="Evidence at rest" flush>
            <div className="vp-rows">
              <div className="vp-row">
                <span className="vp-row-main">
                  <span className="vp-row-title">{ARTIFACTS.length} artifacts</span>
                  <span className="vp-row-sub">
                    All encrypted at rest, inside your {TENANT.runtimeMode} boundary
                  </span>
                </span>
                <span className="vp-row-aside">
                  <Icon name="lock" size={15} />
                </span>
              </div>
              <div className="vp-row">
                <span className="vp-row-main">
                  <span className="vp-row-title">Signing key {TENANT.keyCustody}</span>
                  <span className="vp-row-sub">Excellent cannot sign on your behalf</span>
                </span>
              </div>
              <div className="vp-row">
                <span className="vp-row-main">
                  <span className="vp-row-title">Last sync {fmtRelative("2026-08-17T14:12:00Z")}</span>
                  <span className="vp-row-sub">{fmtDateTime("2026-08-17T14:12:00Z")} · 0 pending events</span>
                </span>
              </div>
            </div>
            <div className="vp-card-foot">
              <Button size="sm" variant="ghost" icon="server" onClick={() => onNavigate(href("runtime"))}>
                Runtime & keys
              </Button>
            </div>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}

/**
 * Next actions.
 *
 * Ranked by what unblocks the most: a source failure is listed above the findings
 * it causes, because fixing the Vanta token and the Jira scope would resolve two
 * UNKNOWN controls at once, whereas working the findings individually would not.
 * A next-action list that just re-sorts the queue is not worth the space.
 */
function NextActions({
  onNavigate,
  open,
}: {
  onNavigate: (to: string) => void;
  open: Finding[];
}) {
  const store = useStore();
  const critical = open.filter((f) => f.severity === "critical");

  return (
    <Card title="Do these next" subtitle="Ordered by how much each one unblocks." flush>
      <div className="vp-rows">
        <button
          type="button"
          className="vp-row"
          data-clickable="true"
          onClick={() => onNavigate(href("connectors"))}
        >
          <span className="vp-row-main">
            <span className="vp-row-title">Fix two broken sources</span>
            <span className="vp-row-sub">
              Replacing the revoked Vanta token and granting Jira <span className="vp-mono">read:jira-user</span>{" "}
              would resolve both UNKNOWN controls at once.
            </span>
          </span>
          <span className="vp-row-aside">
            <Badge tone="fail">2</Badge>
          </span>
        </button>

        <button
          type="button"
          className="vp-row"
          data-clickable="true"
          onClick={() => onNavigate(href("queue"))}
        >
          <span className="vp-row-main">
            <span className="vp-row-title">Decide {critical.length} critical findings</span>
            <span className="vp-row-sub">
              One is escalated to the owner; one is waiting on a re-verification you can trigger now.
            </span>
          </span>
          <span className="vp-row-aside">
            <Badge tone="fail">{critical.length}</Badge>
          </span>
        </button>

        <button
          type="button"
          className="vp-row"
          data-clickable="true"
          onClick={() => onNavigate(href("controls", "ctl_a12"))}
        >
          <span className="vp-row-main">
            <span className="vp-row-title">Map a source to A1.2</span>
            <span className="vp-row-sub">
              Backup restore tests are filed manually, so the control has never been evaluated.
            </span>
          </span>
        </button>

        <button
          type="button"
          className="vp-row"
          data-clickable="true"
          onClick={() => {
            store.reverify("ctl_cc66");
            onNavigate(href("findings", "fnd_4402"));
          }}
        >
          <span className="vp-row-main">
            <span className="vp-row-title">Re-verify CC6.6 after the bucket fix</span>
            <span className="vp-row-sub">
              The owner reports the wildcard grant was removed at 13:58 UTC. The next AWS collection is 16:10.
            </span>
          </span>
        </button>
      </div>
    </Card>
  );
}
